import { exists, mkdir, readTextFile, writeTextFile } from "@tauri-apps/plugin-fs";
import type { DriveMarker } from "@/types";
import { createId } from "@/utils";
import { logger } from "@/services/logger";

const log = logger.scope("drive-layout");

export const DRIVE_ROOT_FOLDER = "SEUFIC";

/** The standard drive layout — created (idempotently) under every root, drive
 * or local fallback alike, so storage providers can assume it always exists. */
export const DRIVE_LAYOUT = [
  "Applications",
  "Models",
  "Memory",
  "Knowledge",
  "Storage",
  "Storage/database",
  "Storage/attachments",
  "Notes",
  "Notes/attachments",
  "Config",
  "Updates",
  "Logs",
  "Cache",
  "Temp",
  "Backups",
] as const;

export const DRIVE_MARKER_RELATIVE_PATH = "Config/drive.json";
const LAYOUT_VERSION = 1;
const APP_VERSION = "0.1.0";

function join(...parts: string[]): string {
  return parts.filter(Boolean).join("/");
}

/** The `SEUFIC/` root for a raw drive mount point (e.g. `D:\` → `D:/SEUFIC`). */
export function seuficRoot(driveMountPoint: string): string {
  const trimmed = driveMountPoint.replace(/[\\/]+$/, "");
  return `${trimmed}/${DRIVE_ROOT_FOLDER}`;
}

async function ensureFolder(path: string): Promise<void> {
  if (!(await exists(path))) {
    await mkdir(path, { recursive: true });
  }
}

/** Creates every folder in `DRIVE_LAYOUT` under `root`, skipping ones that
 * already exist. Safe to call on every connect — this is the "auto-create
 * the standard layout if missing" behaviour. */
export async function ensureDriveLayout(root: string): Promise<void> {
  await ensureFolder(root);
  for (const segment of DRIVE_LAYOUT) {
    await ensureFolder(join(root, segment));
  }
}

async function readMarker(root: string): Promise<DriveMarker | null> {
  const markerPath = join(root, DRIVE_MARKER_RELATIVE_PATH);
  if (!(await exists(markerPath))) return null;
  try {
    const raw = await readTextFile(markerPath);
    return JSON.parse(raw) as DriveMarker;
  } catch (err) {
    log.warn("Drive marker exists but couldn't be parsed; treating as missing", err);
    return null;
  }
}

async function writeMarker(root: string, marker: DriveMarker): Promise<void> {
  await writeTextFile(join(root, DRIVE_MARKER_RELATIVE_PATH), JSON.stringify(marker, null, 2));
}

/**
 * Validates (and self-heals) a candidate root: ensures the full layout
 * exists, then reads or creates the `Config/drive.json` marker. There's no
 * separate "rejected" outcome — a folder that merely lacks the layout is
 * completed, not refused, per "auto-create the structure if missing."
 */
export async function validateDrive(root: string): Promise<DriveMarker> {
  await ensureDriveLayout(root);
  let marker = await readMarker(root);
  if (!marker) {
    marker = {
      driveId: createId("drive"),
      createdAt: Date.now(),
      appVersion: APP_VERSION,
      layoutVersion: LAYOUT_VERSION,
    };
    await writeMarker(root, marker);
    log.info("Initialized new Seufic Drive layout", { root, driveId: marker.driveId });
  }
  return marker;
}
